import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FiChevronRight, FiX } from 'react-icons/fi';
import api from '../../services/api';
import { getWMSSidebarItems } from '../../config/wmsSidebarMenu';
import { useBranding } from '../../contexts/BrandingContext';
import { useTranslation } from '../../contexts/LanguageContext';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { sidebarKey } from '../../i18n/sidebar_translations';

/**
 * Sidebar — pinned rail on desktop, slide-out drawer on mobile.
 *
 * Visuals dropped the Sofbox `.iq-sidebar` / `.iq-menu` classes in
 * favour of inline styles. The drawer behaviour still piggy-backs on
 * `ws-sidebar-drawer` + `is-open` (defined in wareshare-theme.css)
 * because the slide-in transform lives there.
 *
 * Branding-controlled colors (`branding.primary_color`, sidebar bg/
 * text via CSS variables) keep flowing through. Active state uses
 * the brand color for the indicator strip + label.
 */
const WMSSidebar = ({ isMobile = false, mobileOpen = false, onClose }) => {
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const branding = useBranding();
  const { t } = useTranslation();
  const { isEnabled: isFlagEnabled } = useFeatureFlags();
  const role = user?.role || 'TENANT';

  const primary = branding.primary_color || '#7C3AED';
  const platformName = branding.platform_name || 'Khulja Sim Sim';

  // Per-role overrides set by the platform admin; falls back to the
  // hardcoded role lists in wmsSidebarMenu.js.
  const [overrides, setOverrides] = useState([]);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/master-data/menu-visibility/me');
        if (!cancelled) setOverrides(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setOverrides([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const menuItems = getWMSSidebarItems(role, overrides, isFlagEnabled);

  // Map English label → localised version.
  const tLabel = (label) => t(sidebarKey(label), label);

  const [openMenus, setOpenMenus] = useState({});

  // Auto-open the submenu containing the active route.
  useEffect(() => {
    const newOpen = {};
    menuItems.forEach((item) => {
      if (item.children) {
        const isActive = item.children.some(
          (child) => child.path && location.pathname.startsWith(child.path),
        );
        if (isActive) newOpen[item.title] = true;
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...newOpen }));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSubmenu = (title) => {
    setOpenMenus((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const isExactActive = (path) => location.pathname === path;
  const isPathActive = (path) => location.pathname.startsWith(path);
  const isChildActive = (children) =>
    children.some((child) => child.path && location.pathname.startsWith(child.path));

  // ``ws-sidebar-drawer`` + ``is-open`` are defined in wareshare-theme.css
  // and drive the mobile slide-in transform. Keep them on the root.
  const drawerClasses = [
    'iq-sidebar', // legacy hook — kept so the body width offset still applies
    isMobile ? 'ws-sidebar-drawer' : '',
    isMobile && mobileOpen ? 'is-open' : '',
  ].filter(Boolean).join(' ');

  return (
    <aside
      className={drawerClasses}
      aria-hidden={isMobile && !mobileOpen ? 'true' : 'false'}
      style={{
        display: 'flex', flexDirection: 'column',
        height: '100vh', overflow: 'hidden',
        background: 'var(--wms-sidebar-bg, #ffffff)',
        color: 'var(--wms-sidebar-text, #222222)',
        borderRight: '1px solid #ebebeb',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      {/* ── Logo block ──────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12,
        padding: '16px 20px',
        borderBottom: '1px solid var(--wms-sidebar-border, #ebebeb)',
        flexShrink: 0,
      }}>
        <NavLink
          to="/dashboard"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            textDecoration: 'none', minWidth: 0, color: 'inherit',
          }}
        >
          {/* Icon + wordmark — same brand pair as the public + auth
             surfaces. ``maxWidth`` on the wordmark caps it within the
             sidebar rail so the brand block doesn't squeeze the
             close-button into the next line on narrow viewports. */}
          <img
            src={branding.logo_url || '/logo-icon.png'}
            alt={platformName}
            style={{
              height: 38, maxWidth: 48, objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          <img
            src={branding.logo_wordmark_url || '/logo-wordmark.png'}
            alt={platformName}
            style={{
              height: 18, objectFit: 'contain',
              maxWidth: '100%', minWidth: 0, flexShrink: 1,
            }}
          />
        </NavLink>
        {isMobile && (
          <button
            type="button"
            aria-label="Close navigation"
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              border: 'none', background: 'transparent',
              color: 'currentColor', cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      {/* ── Scrollable menu ─────────────────────────────────── */}
      <nav
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '12px 0 24px',
        }}
      >
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {menuItems.map((item, idx) => {
            // ── Section heading ──
            if (item.type === 'heading') {
              return (
                <li
                  key={`heading-${idx}`}
                  style={{
                    padding: '14px 20px 6px',
                    marginTop: idx > 0 ? 8 : 0,
                    fontSize: 11, fontWeight: 700,
                    color: 'var(--wms-sidebar-text, #717171)',
                    opacity: 0.65,
                    letterSpacing: '0.12em', textTransform: 'uppercase',
                  }}
                >
                  {tLabel(item.label)}
                </li>
              );
            }

            // ── Simple link (no children) ──
            if (!item.children) {
              const active = isExactActive(item.path) || isPathActive(item.path);
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    style={({ isActive }) => menuRowStyle({
                      active: active || isActive, primary,
                    })}
                  >
                    <ActiveBar visible={active} primary={primary} />
                    {item.icon && (
                      <i
                        className={item.icon}
                        style={{ fontSize: '1.05rem', width: 22, textAlign: 'center', flexShrink: 0 }}
                      />
                    )}
                    <span style={{
                      fontSize: 14, fontWeight: active ? 600 : 500,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {tLabel(item.title)}
                    </span>
                  </NavLink>
                </li>
              );
            }

            // ── Parent with children ──
            const isOpen = !!openMenus[item.title];
            const hasActiveChild = isChildActive(item.children);

            return (
              <li key={item.title}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => toggleSubmenu(item.title)}
                  style={{
                    ...menuRowStyle({ active: hasActiveChild, primary }),
                    width: '100%', border: 'none', cursor: 'pointer',
                    background: 'transparent', textAlign: 'left',
                  }}
                >
                  <ActiveBar visible={hasActiveChild} primary={primary} />
                  {item.icon && (
                    <i
                      className={item.icon}
                      style={{ fontSize: '1.05rem', width: 22, textAlign: 'center', flexShrink: 0 }}
                    />
                  )}
                  <span style={{
                    flex: 1, fontSize: 14, fontWeight: hasActiveChild ? 600 : 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {tLabel(item.title)}
                  </span>
                  <FiChevronRight
                    size={14}
                    style={{
                      transition: 'transform 200ms ease',
                      transform: isOpen ? 'rotate(90deg)' : 'rotate(0)',
                      opacity: 0.6,
                      flexShrink: 0,
                    }}
                  />
                </button>

                {isOpen && (
                  <ul style={{
                    listStyle: 'none', padding: '4px 0', margin: 0,
                    background: 'rgba(0,0,0,0.03)',
                  }}>
                    {item.children.map((child) => {
                      const childActive = isExactActive(child.path);
                      return (
                        <li key={child.path}>
                          <NavLink
                            to={child.path}
                            style={() => ({
                              display: 'flex', alignItems: 'center', gap: 12,
                              padding: '8px 20px 8px 54px',
                              color: childActive
                                ? primary
                                : 'var(--wms-sidebar-text, #484848)',
                              background: childActive
                                ? `${primary}14`
                                : 'transparent',
                              fontSize: 13,
                              fontWeight: childActive ? 600 : 500,
                              textDecoration: 'none',
                              transition: 'background 120ms ease, color 120ms ease',
                              position: 'relative',
                            })}
                          >
                            {child.icon ? (
                              <i
                                className={child.icon}
                                style={{
                                  fontSize: '0.85rem', width: 16, textAlign: 'center',
                                  opacity: childActive ? 1 : 0.55,
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <span aria-hidden style={{
                                width: 4, height: 4, borderRadius: '50%',
                                background: 'currentColor', opacity: 0.4,
                                flexShrink: 0,
                              }} />
                            )}
                            <span style={{
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {tLabel(child.title)}
                            </span>
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </aside>
  );
};

// ── Helpers ─────────────────────────────────────────────────────────

const menuRowStyle = ({ active, primary }) => ({
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 20px',
  color: active ? primary : 'var(--wms-sidebar-text, #222222)',
  background: active ? `${primary}10` : 'transparent',
  textDecoration: 'none',
  transition: 'background 120ms ease, color 120ms ease',
  position: 'relative',
});

// 3px brand-colored bar on the left edge of an active row.
const ActiveBar = ({ visible, primary }) => (
  <span
    aria-hidden
    style={{
      position: 'absolute', left: 0, top: 4, bottom: 4,
      width: 3, borderRadius: '0 3px 3px 0',
      background: visible ? primary : 'transparent',
    }}
  />
);

export default WMSSidebar;
